import React, { useCallback, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import strings from 'utils/strings'
import featuresStrings from 'utils/strings/features-strings'
import * as yup from 'yup'
import { useSelector, useMakeSelector } from 'hooks/redux'
import { makeGetIsWaiting } from 'selectors/application'
import { useDispatch } from 'react-redux'
import { isEmpty, propOr, propEq, prop, includes } from 'ramda'
import { useHistory, useLocation } from 'react-router-dom'
import {
  setDropdownSelectionByKey,
  createAndAddFilterTerm,
  handleRemoveFilterTerm,
  handleClearFilterTerms,
  clearDropdownByKey,
  ADS_SET_FILTER_DATA_BY_COLLECTION,
} from 'actions/search'
import {
  getDropdownSelectionSelector,
  getFiltersByCollection,
  selectAdvancedDropdownOptionsByGroup,
  selectFirstDropdownOptionsList,
} from 'selectors/search'
import {
  FIRST_DROPDOWN,
  SECOND_DROPDOWN,
  THIRD_DROPDOWN,
  FOURTH_DROPDOWN,
  FIFTH_DROPDOWN,
  FEATURE,
  LOGIC_SET,
  INTERFACE_CONNECTOR,
  FUNCTION,
  NATIVE_INPUTS,
  NATIVE_OUTPUTS,
  SWITCHES,
  USER,
  FEATURE_COLLECTIONS,
} from 'utils/constants'
import { MODULE_TYPE } from 'utils/constants/vehicle-constants'
import { Header, Loading } from 'components/patterns'
import SelectMenu from 'components/patterns/Inputs/SelectMenu'
import AdvancedFeaturesChipFilters from 'components/AdvanceFeaturesChipFilters/AdvancedFeaturesChipFilters'
import FastFeaturesEcuChipFilters from 'components/FastFeaturesEcuChipFilters'
import {
  AdvancedSearchFiltersHeader,
  ChipHeader,
  ActiveFilters,
  ClearFilters,
  StyledSelectMenu,
  ChipFilterBox,
  StyledChipList,
  StyledWidgetHeader,
  StyledCollapsibleWidget,
  StyledParagraph,
  AdvancedFilterBox,
} from './AdvancedFeaturesSearchFilters.style'

// XMC3 has only 6 optional solenoids
const xmc3Solenoids = [
  'Optional 1',
  'Optional 2',
  'Optional 3',
  'Optional 4',
  'Optional 5',
  'Optional 6',
]

const solenoidOptions = [
  { key: 'solenoid-na', value: 'N/A', title: 'N/A' },
  ...xmc3Solenoids.map((type, index) => ({
    key: `solenoid-${index + 1}`,
    value: type,
    title: type,
  })),
]

const collectionName = FEATURE

const AdvancedFeaturesSearchFilters = ({
  hideChips,
  searchCollectionName,
  searchString,
  isSearching,
  applyFilters,
  render,
  isHorizontalLayout,
  onSolenoidChange,
  isFastFeatures,
  addToSearchRequestBody,
  filterModules,
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const selectedSolenoidType = params.get('solenoid') || null

  const loading = useMakeSelector(makeGetIsWaiting, {
    signals: [`${ADS_SET_FILTER_DATA_BY_COLLECTION}${collectionName}`],
  })
  const firstDropdownOptions = useSelector(selectFirstDropdownOptionsList, {
    collectionName,
    loading,
  })
  const firstDropdownSelection = useSelector(getDropdownSelectionSelector, {
    dropdownKey: FIRST_DROPDOWN,
    collectionName,
  })
  const secondDropdownOptions = useSelector(
    selectAdvancedDropdownOptionsByGroup,
    {
      collectionName,
      dropdownKey: SECOND_DROPDOWN,
      selection: firstDropdownSelection,
    }
  )
  const secondDropdownSelection = useSelector(getDropdownSelectionSelector, {
    dropdownKey: SECOND_DROPDOWN,
    collectionName,
  })

  const thirdDropdownOptions = useSelector(
    selectAdvancedDropdownOptionsByGroup,
    {
      collectionName,
      dropdownKey: THIRD_DROPDOWN,
      selection: secondDropdownSelection,
    }
  )
  const thirdDropdownSelection = useSelector(getDropdownSelectionSelector, {
    dropdownKey: THIRD_DROPDOWN,
    collectionName,
  })

  const fourthDropdownOptions = useSelector(
    selectAdvancedDropdownOptionsByGroup,
    {
      collectionName,
      dropdownKey: FOURTH_DROPDOWN,
      selection: thirdDropdownSelection,
    }
  )
  const fourthDropdownSelection = useSelector(getDropdownSelectionSelector, {
    dropdownKey: FOURTH_DROPDOWN,
    collectionName,
  })

  const fifthDropdownOptions = useSelector(
    selectAdvancedDropdownOptionsByGroup,
    {
      collectionName,
      dropdownKey: FIFTH_DROPDOWN,
      selection: fourthDropdownSelection,
    }
  )
  const fifthDropdownSelection = useSelector(getDropdownSelectionSelector, {
    dropdownKey: FIFTH_DROPDOWN,
    collectionName,
  })

  const activeFilters = useSelector(getFiltersByCollection, collectionName)
  const hasTwoDropdown =
    propEq('id', NATIVE_INPUTS, firstDropdownSelection) ||
    propEq('id', NATIVE_OUTPUTS, firstDropdownSelection) ||
    propEq('id', SWITCHES, firstDropdownSelection) ||
    propEq('id', USER, firstDropdownSelection)

  const hasThreeDropdown = includes(prop('id', firstDropdownSelection), [
    INTERFACE_CONNECTOR,
    LOGIC_SET,
    FUNCTION,
  ])
  const hasFiveDropdown = propEq('id', MODULE_TYPE, firstDropdownSelection)
  // dropdown change handlers
  const onFirstDropdownChange = useCallback(
    (selectedValue) => {
      dispatch(
        setDropdownSelectionByKey(selectedValue, collectionName, FIRST_DROPDOWN)
      )
      dispatch(clearDropdownByKey(collectionName, SECOND_DROPDOWN))
    },
    [dispatch]
  )

  const onSecondDropdownChange = useCallback(
    (secondSelectedValue) => {
      dispatch(
        setDropdownSelectionByKey(
          secondSelectedValue,
          collectionName,
          SECOND_DROPDOWN
        )
      )
      if (hasTwoDropdown) {
        dispatch(createAndAddFilterTerm(collectionName, SECOND_DROPDOWN))
        // A value has to be set because the input is controlled
        dispatch(
          setDropdownSelectionByKey(
            secondSelectedValue,
            collectionName,
            SECOND_DROPDOWN
          )
        )
      }
      dispatch(clearDropdownByKey(collectionName, THIRD_DROPDOWN))
    },
    [dispatch, hasTwoDropdown]
  )

  // we have to control and clear the input
  // if not a multi filter and second select has value, code need to clear it
  const isOnlyTwoDropdowns = !hasThreeDropdown && !hasFiveDropdown
  useEffect(() => {
    if (isOnlyTwoDropdowns && !isEmpty(secondDropdownSelection)) {
      dispatch(clearDropdownByKey(collectionName, SECOND_DROPDOWN))
    }
  }, [secondDropdownSelection, dispatch, isOnlyTwoDropdowns])

  const onThirdDropdownChange = useCallback(
    (thirdSelectedValue) => {
      dispatch(
        setDropdownSelectionByKey(
          thirdSelectedValue,
          collectionName,
          THIRD_DROPDOWN
        )
      )
      if (hasThreeDropdown) {
        dispatch(createAndAddFilterTerm(collectionName, THIRD_DROPDOWN))
      }
      dispatch(clearDropdownByKey(collectionName, FOURTH_DROPDOWN))
      dispatch(clearDropdownByKey(collectionName, FIFTH_DROPDOWN))
    },
    [dispatch, hasThreeDropdown]
  )

  const onFourthDropdownChange = useCallback(
    (fourthSelectedValue) => {
      dispatch(
        setDropdownSelectionByKey(
          fourthSelectedValue,
          collectionName,
          FOURTH_DROPDOWN
        )
      )

      dispatch(clearDropdownByKey(collectionName, FIFTH_DROPDOWN))
    },
    [dispatch]
  )

  const onFifthDropdownChange = useCallback(
    (fifthSelectedValue) => {
      dispatch(
        setDropdownSelectionByKey(
          fifthSelectedValue,
          collectionName,
          FIFTH_DROPDOWN
        )
      )
      dispatch(createAndAddFilterTerm(collectionName, FIFTH_DROPDOWN))
    },
    [dispatch]
  )

  // clear filter or chip handlers
  const handleChipRemove = useCallback(
    (value) => {
      dispatch(handleRemoveFilterTerm(value, collectionName))
    },
    [dispatch]
  )
  const handleClearFiltersDefault = () => {
    dispatch(handleClearFilterTerms(collectionName))
  }

  const handleSolenoidFilter = useCallback(
    (value) => {
      if (value) {
        params.set('solenoid', value)
      } else {
        params.delete('solenoid')
      }
      history.push({
        search: `?${params.toString()}`,
      })
      if (onSolenoidChange) {
        onSolenoidChange(value || null)
      }
    },
    [history, params, onSolenoidChange]
  )

  // Clear solenoid filter when component unmounts
  useEffect(() => {
    return () => {
      const currentParams = new URLSearchParams(window.location.search)
      if (currentParams.has('solenoid')) {
        currentParams.delete('solenoid')
        history.replace({
          search: `?${currentParams.toString()}`,
        })
      }
    }
  }, [history])
  // ECU--->ASAM/XMC1/XMC2 etc(schema)--->input/output--->type/pin/desc--->their values(schema) === 2 filter terms
  // Interface/Logic Set/Function--->type/number/description---> theirvalues(schema) === 1 filter term
  // NativeSignals--> their value(schema) === 1 filter term
  // not working --> logic set and functions
  const hasSecondDropdownOptions = !isEmpty(secondDropdownOptions)
  const hasThirdDropdownOptions = !isEmpty(thirdDropdownOptions)
  const hasFourthDropdownOptions = !isEmpty(fourthDropdownOptions)
  const hasFifthDropdownOptions = !isEmpty(fifthDropdownOptions)
  const getSelectedValue = propOr(null, 'selectedValue')

  const Loader = <Loading contained={true} size="sm" />
  const Filters = (
    <>
      <div>
        <StyledSelectMenu
          key={FIRST_DROPDOWN}
          loading={loading}
          vertical
          value={getSelectedValue(firstDropdownSelection)}
          onChange={onFirstDropdownChange}
          name={FIRST_DROPDOWN}
          showSearch
          filterOption
          placeholder={strings.select_criteria}
          options={firstDropdownOptions}
          isHorizontalLayout={isHorizontalLayout}
          rules={yup.string().nullable()}
          getPopupContainer={() => document.body}
        />
        <StyledSelectMenu
          key={SECOND_DROPDOWN}
          loading={loading}
          disabled={!hasSecondDropdownOptions}
          vertical
          value={getSelectedValue(secondDropdownSelection)}
          onChange={onSecondDropdownChange}
          name={SECOND_DROPDOWN}
          showSearch
          filterOption
          placeholder={strings.select_value}
          options={secondDropdownOptions}
          isHorizontalLayout={isHorizontalLayout}
          rules={yup.string().nullable()}
          getPopupContainer={() => document.body}
        />
        {(hasThreeDropdown || hasFiveDropdown) && (
          <StyledSelectMenu
            key={THIRD_DROPDOWN}
            loading={loading}
            disabled={!hasThirdDropdownOptions}
            vertical
            value={getSelectedValue(thirdDropdownSelection)}
            onChange={onThirdDropdownChange}
            name={THIRD_DROPDOWN}
            showSearch
            filterOption
            placeholder={strings.select_criteria}
            options={thirdDropdownOptions}
            isHorizontalLayout={isHorizontalLayout}
            rules={yup.string().nullable()}
            getPopupContainer={() => document.body}
          />
        )}
        {hasFiveDropdown && (
          <>
            <StyledSelectMenu
              key={FOURTH_DROPDOWN}
              loading={loading}
              disabled={!hasFourthDropdownOptions}
              vertical
              value={getSelectedValue(fourthDropdownSelection)}
              onChange={onFourthDropdownChange}
              name={FOURTH_DROPDOWN}
              showSearch
              filterOption
              placeholder={strings.select_criteria}
              options={fourthDropdownOptions}
              isHorizontalLayout={isHorizontalLayout}
              rules={yup.string().nullable()}
              getPopupContainer={() => document.body}
            />
            <StyledSelectMenu
              key={FIFTH_DROPDOWN}
              loading={loading}
              disabled={!hasFifthDropdownOptions}
              vertical
              value={getSelectedValue(fifthDropdownSelection)}
              onChange={onFifthDropdownChange}
              name={FIFTH_DROPDOWN}
              showSearch
              filterOption
              placeholder={strings.select_criteria}
              options={fifthDropdownOptions}
              isHorizontalLayout={isHorizontalLayout}
              rules={yup.string().nullable()}
              getPopupContainer={() => document.body}
            />
          </>
        )}
      </div>
    </>
  )

  const collapsibleWidgetHeader = (
    <StyledWidgetHeader>{strings.filters}</StyledWidgetHeader>
  )
  const ChipFiltersContent = isFastFeatures ? (
    <FastFeaturesEcuChipFilters
      searchString={searchString}
      isSearching={isSearching}
      isHorizontalLayout={isHorizontalLayout}
      addToSearchRequestBody={addToSearchRequestBody}
    />
  ) : (
    <AdvancedFeaturesChipFilters
      key={filterModules ? filterModules.join(',') : 'all'}
      searchString={searchString}
      isSearching={isSearching}
      applyFilters={applyFilters}
      isHorizontalLayout={isHorizontalLayout}
      collectionName={searchCollectionName}
      filterModules={filterModules}
    />
  )
  const ChipFilters = (
    <ChipFilterBox isHorizontalLayout={isHorizontalLayout}>
      {!isHorizontalLayout && <Header type="h3">{strings.filters}</Header>}
      {ChipFiltersContent}
    </ChipFilterBox>
  )
  const ChipFiltersArea = isHorizontalLayout ? (
    <StyledCollapsibleWidget
      header={collapsibleWidgetHeader}
      className="add-feature-chip-filters"
      panelClassName="feature-settings"
      activeKey="0"
    >
      {ChipFilters}
    </StyledCollapsibleWidget>
  ) : (
    ChipFilters
  )

  const activeFiltersLength = useMemo(
    () => (!isEmpty(activeFilters) ? Object.values(activeFilters).length : -1),
    [activeFilters]
  )

  const AdvancedFilterComponent = loading ? Loader : Filters
  const advancedFiltersHeader = (
    <StyledParagraph
      style={{
        color: 'var(--Black-Black-Secondary, rgba(7, 7, 15, 0.70))',
        fontFeatureSettings: "'liga' off, 'clig' off",
        fontFamily: 'Arial',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '18px',
        marginBottom: '4px',
      }}
    >{`${strings.advaned_filters}`}</StyledParagraph>
  )
  const advancedFiltersTitle = isHorizontalLayout ? (
    advancedFiltersHeader
  ) : (
    <AdvancedSearchFiltersHeader>
      <Header type="h3">{`${strings.advaned_filters} ${
        activeFiltersLength > -1 ? `(${activeFiltersLength})` : ``
      }`}</Header>
      {activeFiltersLength > -1 && (
        <ClearFilters onClick={handleClearFiltersDefault}>
          {strings.clear}
        </ClearFilters>
      )}
    </AdvancedSearchFiltersHeader>
  )
  const AdvancedFilter = (
    <AdvancedFilterBox isHorizontalLayout={isHorizontalLayout}>
      {advancedFiltersTitle}
      {AdvancedFilterComponent}
    </AdvancedFilterBox>
  )

  const Chips = (
    <>
      {isHorizontalLayout && (
        <ChipHeader>
          <ActiveFilters>{strings.active_filters}</ActiveFilters>
          {activeFiltersLength > -1 && (
            <ClearFilters onClick={handleClearFiltersDefault}>
              {strings.clear}
            </ClearFilters>
          )}
        </ChipHeader>
      )}
      {!hideChips && !isEmpty(activeFilters) && (
        <StyledChipList
          chips={activeFilters}
          remove={handleChipRemove}
          isHorizontalLayout={isHorizontalLayout}
        />
      )}
    </>
  )

  const SolenoidFilter = (
    <div style={{ width: '140px' }}>
      <SelectMenu
        label={
          isHorizontalLayout
            ? featuresStrings.ff_column_solenoid
            : featuresStrings.ff_filter_by_solenoid
        }
        name="solenoid-type"
        onChange={handleSolenoidFilter}
        options={solenoidOptions}
        value={selectedSolenoidType}
        placeholder={strings.select_criteria}
        allowClear
        rules={yup.string().nullable()}
        vertical
      />
    </div>
  )

  return render(ChipFiltersArea, AdvancedFilter, Chips, SolenoidFilter)
}

export default AdvancedFeaturesSearchFilters

AdvancedFeaturesSearchFilters.propTypes = {
  hideChips: PropTypes.bool,
  searchString: PropTypes.string,
  searchCollectionName: PropTypes.oneOf([FEATURE, FEATURE_COLLECTIONS]),
  isSearching: PropTypes.bool,
  applyFilters: PropTypes.func,
  serialNumber: PropTypes.string,
  isHorizontalLayout: PropTypes.bool,
  onSolenoidChange: PropTypes.func,
  isFastFeatures: PropTypes.bool,
  addToSearchRequestBody: PropTypes.object,
  filterModules: PropTypes.arrayOf(PropTypes.string),
}
